import { LearningOpportunityType } from "@/server/backend-api/generated";
import { mcn, useFitTextToContainer } from "@/utils";
import { useRef } from "react";
import { AnchorLinks, AnchorLinksProps } from "../AnchorLinks/AnchorLinks";
import { StatusLabel, StatusLabelProps } from "../StatusLabel/StatusLabel";

type Props = {
  type?: LearningOpportunityType;
  typeLabel?: string;
  title?: string | null;
  subTitle?: string | null;
  image?: React.ReactElement | null;
  labels?: StatusLabelProps[];
  anchorLinksProps?: AnchorLinksProps;
};

const ObjectHeader = ({
  title = "",
  subTitle,
  type,
  typeLabel,
  labels,
  image,
  anchorLinksProps,
}: Props) => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useFitTextToContainer(titleRef, 32, 72);

  const hasImage = !!image;
  return (
    <>
      <div
        className={mcn(
          "bg-blue text-white bg-object-header-gradient",
          "pt-xl pb-[56px]",
          "laptop:before:block laptop:before:h-site-header-desktop",
          "overflow-x-hidden",
          anchorLinksProps && "object-header-height"
        )}
      >
        <div className="container">
          <div
            className={mcn(
              "relative flex flex-col items-center",
              "tablet-lg:flex-row tablet-lg:items-start"
            )}
          >
            {hasImage && (
              <div
                className={mcn(
                  "w-[208px]",
                  "mr-0 tablet-lg:mr-lg",
                  "mb-lg tablet-lg:mb-0",
                  "aspect-square flex-shrink-0"
                )}
              >
                {image}
              </div>
            )}
            <div
              className={mcn(
                "min-w-0 max-w-full flex-grow", // min-width:0 to prevent flex element from overflow and instead wrap content
                "text-center tablet-lg:text-left"
              )}
            >
              <div
                className={mcn(
                  "mb-xs tablet-lg:mb-md",
                  "text-style-subtitle1 tablet-lg:text-style-headline3 uppercase"
                )}
              >
                {typeLabel ?? type}
              </div>
              <h1
                ref={titleRef}
                className={mcn(
                  "hidden tablet-lg:inline-block whitespace-nowrap", // must be inline-block and nowrap for useFitTextToContainer()
                  "mb-xs",
                  "text-style-headline1 !leading-[1.33]" // Base style on headline1 but adjust line-height (leading) to be relative to calulated font-size
                )}
              >
                {title}
              </h1>
              <h1
                className={mcn("tablet-lg:hidden mb-xs text-style-headline3 ")}
              >
                {title}
              </h1>
              <div className="text-style-body2  tablet-lg:text-style-headline4 uppercase line-clamp-3">
                {subTitle}
              </div>
            </div>

            <div className="absolute -top-sm tablet-lg:top-0 right-0 space-x-2xs">
              {labels?.map((label, index) => (
                <StatusLabel key={index} {...label} />
              ))}
            </div>
          </div>
        </div>
      </div>
      {anchorLinksProps && (
        <AnchorLinks
          {...anchorLinksProps}
          //  negative margin to move links up and "under" object header gradient
          className="-mt-[56px] h-[56px] "
        />
      )}
    </>
  );
};

export default ObjectHeader;
